import { useRef, useState, useEffect } from 'react';

const DEFAULT_FADE_DURATION = 1000;
const FADE_INITIAL_CLASS = 'fade-initial';
const FADE_IN_CLASS = 'fade-in';
const FADE_OUT_CLASS = 'fade-out';

interface IParams {
    dependency: unknown;
    fadeDuration?: number;
    delayDuration?: number;
    runInitial?: boolean;
    disableFadeOut?: boolean;
    classes?: {
        fadeInitial: string;
        fadeIn: string;
        fadeOut?: string;
    };
}

/**
 * useFade hook has 3 states:
 * - fade-initial - element should be hidden
 * - fade-in - element is fading in and is visible for `delayDuration` time
 * - fade-out - element is fading out
 */
const useFade = (props: IParams): string => {
    const fadeInitialClass = props.classes?.fadeInitial ?? FADE_INITIAL_CLASS;
    const fadeInClass = props.classes?.fadeIn ?? FADE_IN_CLASS;
    const fadeOutClass = props.classes?.fadeOut ?? FADE_OUT_CLASS;
    const skipInitial = useRef<boolean>(true);
    const [fadeClass, setFadeClass] = useState<string>(fadeInitialClass);

    const { fadeDuration = DEFAULT_FADE_DURATION, delayDuration = 0 } = props;

    useEffect(() => {
        if (!props.runInitial && skipInitial.current) {
            skipInitial.current = false;
            return undefined;
        }

        if (typeof props.dependency === 'undefined') {
            return undefined;
        }

        setFadeClass(fadeInitialClass);

        const initialFadeTimeoutId = window.setTimeout(() => {
            setFadeClass(fadeInClass);
        }, 0);

        let delayTimeoutId: number;
        let fadeOutTimeoutId: number;

        if (!props.disableFadeOut) {
            delayTimeoutId = window.setTimeout(() => {
                setFadeClass(fadeOutClass);
            }, fadeDuration + delayDuration);

            fadeOutTimeoutId = window.setTimeout(
                () => {
                    setFadeClass(fadeInitialClass);
                },
                fadeDuration + delayDuration + fadeDuration,
            );
        }

        return () => {
            window.clearTimeout(initialFadeTimeoutId);
            window.clearTimeout(delayTimeoutId);
            window.clearTimeout(fadeOutTimeoutId);
        };
    }, [props.dependency]);

    return fadeClass;
};

export default useFade;
